import { useEffect, useState } from "react";
import type { useBlog } from "../../controllers/useBlog";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import { UserInline, humanizeMentions, type UserDir } from "../components/UserCell";
import type { PostComment } from "../../models/project";

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** A minimal, safe (escape-first) markdown → HTML renderer: headings, bold/italic, code, links, lists. */
function renderMarkdown(md: string): string {
  const lines = escapeHtml(md).split("\n");
  const blocks: string[] = [];
  let listBuf: string[] = [];
  const flushList = () => {
    if (listBuf.length) { blocks.push(`<ul>${listBuf.join("")}</ul>`); listBuf = []; }
  };
  let paraBuf: string[] = [];
  const flushPara = () => {
    if (paraBuf.length) { blocks.push(`<p>${paraBuf.join("<br/>")}</p>`); paraBuf = []; }
  };
  const inline = (s: string) =>
    s
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/_([^_]+)_/g, "<em>$1</em>")
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      flushList(); flushPara();
      const level = heading[1].length;
      blocks.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      continue;
    }
    if (/^-\s+/.test(line)) {
      flushPara();
      listBuf.push(`<li>${inline(line.replace(/^-\s+/, ""))}</li>`);
      continue;
    }
    flushList();
    if (line === "") {
      flushPara();
    } else {
      paraBuf.push(inline(line));
    }
  }
  flushList();
  flushPara();
  return blocks.join("\n");
}

// One post's detail: rendered markdown body, threaded comments/replies, add comment/reply.
export function PostDetailView({ blog }: { blog: ReturnType<typeof useBlog> }) {
  const post = blog.selectedPost!;
  const topLevel = blog.comments.filter((c) => !c.parentCommentId);
  const repliesOf = (id: string) => blog.comments.filter((c) => c.parentCommentId === id);

  const dir = useUserDirectory(blog.getToken);
  useEffect(() => {
    dir.ensure([post.authorId, ...blog.comments.map((c) => c.authorId)]);
  }, [post.authorId, blog.comments, dir.ensure]);

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
        <button className="btn ghost" style={{ width: "auto" }} onClick={blog.backToPostFeed}>← 블로그</button>
        {post.status !== "PUBLISHED" ? (
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => void blog.publish(post.id)}>게시</button>
        ) : (
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => void blog.archive(post.id)}>보관</button>
        )}
        <button className="btn ghost" style={{ width: "auto" }} onClick={() => blog.openEditor(post.id)}>편집</button>
        <button className="btn ghost" style={{ width: "auto" }} onClick={() => {
          if (window.confirm("이 글을 삭제하시겠습니까?")) void blog.removePost(post.id);
        }}>삭제</button>
      </div>

      {blog.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}><b style={{ color: "var(--amber)" }}>오류:</b> {blog.error}</div>}

      <div className="detail-name"><h1>{post.title}</h1></div>
      <p className="meta">작성자 <UserInline id={post.authorId} dir={dir} /></p>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {post.tags.map((t) => <span key={t} className="tag">#{t}</span>)}
      </div>

      {blog.loadingDetail ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <>
          <div className="markdown-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }} />

          <h3 style={{ marginTop: 28 }}>댓글 · {blog.comments.length}</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {topLevel.map((c) => (
              <CommentThread key={c.id} comment={c} replies={repliesOf(c.id)} blog={blog} dir={dir} />
            ))}
            {topLevel.length === 0 && <div className="sdui-list-empty">댓글이 없습니다.</div>}
          </div>
          <NewComment blog={blog} parentCommentId={null} />
        </>
      )}
    </div>
  );
}

function CommentThread({
  comment, replies, blog, dir,
}: { comment: PostComment; replies: PostComment[]; blog: ReturnType<typeof useBlog>; dir: UserDir }) {
  const [replying, setReplying] = useState(false);
  return (
    <div className="screen-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
      <span style={{ fontSize: 12, color: "var(--ink3)" }}>
        <b><UserInline id={comment.authorId} dir={dir} /></b> · {new Date(comment.createdAt).toLocaleString()}
      </span>
      <span style={{ fontSize: 14 }}>{humanizeMentions(comment.body, dir)}</span>
      <div>
        <button className="link-btn" onClick={() => setReplying((r) => !r)}>답글</button>
        <button className="link-btn" onClick={() => {
          if (window.confirm("이 댓글을 삭제하시겠습니까?")) void blog.removeComment(comment.id);
        }}>삭제</button>
      </div>
      {replies.length > 0 && (
        <div style={{ marginLeft: 20, display: "flex", flexDirection: "column", gap: 8 }}>
          {replies.map((r) => (
            <div key={r.id} style={{ borderLeft: "2px solid var(--border)", paddingLeft: 10 }}>
              <span style={{ fontSize: 12, color: "var(--ink3)" }}>
                <b><UserInline id={r.authorId} dir={dir} /></b> · {new Date(r.createdAt).toLocaleString()}
              </span>
              <div style={{ fontSize: 14 }}>{humanizeMentions(r.body, dir)}</div>
            </div>
          ))}
        </div>
      )}
      {replying && <NewComment blog={blog} parentCommentId={comment.id} onDone={() => setReplying(false)} />}
    </div>
  );
}

function NewComment({
  blog, parentCommentId, onDone,
}: { blog: ReturnType<typeof useBlog>; parentCommentId: string | null; onDone?: () => void }) {
  const [body, setBody] = useState("");

  async function submit() {
    if (!body.trim()) return;
    const mentioned = [...body.matchAll(/@([0-9a-fA-F-]{36})/g)].map((m) => m[1]);
    await blog.addComment({ parentCommentId: parentCommentId ?? undefined, body, mentionedUserIds: mentioned });
    setBody("");
    onDone?.();
  }

  return (
    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
      <input
        className="sdui-input"
        placeholder={parentCommentId ? "답글 작성" : "댓글 작성 (@사용자ID 로 멘션)"}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
      />
      <button className="btn primary" style={{ width: "auto" }} disabled={!body.trim()} onClick={submit}>등록</button>
    </div>
  );
}
